import React, { useState, useEffect } from 'react';
import { Trophy, Users, Calendar, ArrowLeft, Target, AlertCircle, RefreshCw, Lock, Skull, Award, Crown, BarChart3, Activity, Shield, MessageSquare, CalendarClock, RotateCcw, Radio } from 'lucide-react';
import Banterboard from '../banter/Banterboard';
import FixtureSelection from './FixtureSelection';
import FixturePlanner from '../picks/FixturePlanner';
import GameProgress from './GameProgress';
import LiveRound from './LiveRound';
import RoundAnalytics from './RoundAnalytics';
import TeamFormTable from './TeamFormTable';
import GafferPanel from './GafferPanel';
import RolloverPanel from './RolloverPanel';
import SplitPrizePanel from './SplitPrizePanel';
import CreateLeagueFromGamePanel from './CreateLeagueFromGamePanel';
import InvitePeopleModal from '../invitations/InvitePeopleModal';
import AdSlot from '../../components/AdSlot';
import { fetchTeamForms } from '../../api/teamFormApi';
import * as gamesApi from '../../api/games';
import { isGameCompleted as isGameCompletedShared, rolloverLabel } from '../games/gameStatus';
import colors from '../../styles/theme';

function GameDetailsPage({ gameId, currentUser, initialTab, onBack, onOpenSponsor }) {
  const [game, setGame] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [teamForm, setTeamForm] = useState({});
  const [usedTeams, setUsedTeams] = useState([]);
  const [currentPick, setCurrentPick] = useState(null);
  const [allPicks, setAllPicks] = useState([]);
  const [gameweekStatus, setGameweekStatus] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedFixtureEntry, setSelectedFixtureEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [activeTab, setActiveTab] = useState('picks');
  const [showInvite, setShowInvite] = useState(false);

  /**
   * Where a link inside a Gaffer post lands.
   *
   * The server sends a target name rather than a URL, because this app has no router and
   * a href would be a full page load to somewhere that does not exist. Both of the names
   * it can send happen to be tabs on this page, which is not a coincidence — the posts
   * that carry a link are about this round, and this is the page the round lives on.
   *
   * Allow-listed rather than passed straight to `setActiveTab`: a post written months ago
   * naming a tab that has since gone would otherwise blank the page, and a stored payload
   * is not something this component gets to validate at write time.
   */
  const openTabFromPost = (target) => {
    if (target === 'progress' || target === 'picks') {
      setActiveTab(target);
    }
  };

  const isGameCompleted = () => (game ? isGameCompletedShared(game) : false);

  const isWinner = () => {
    if (!game || !currentUser || !isGameCompleted()) return false;
    const remainingIds = game.remainingPlayers?.map(p => p.id) || [];
    return remainingIds.includes(currentUser.id);
  };

  const isEliminated = () => {
    if (!game || !currentUser) return false;
    if (isGameCompleted()) return false;
    const remainingIds = game.remainingPlayers?.map(p => p.id) || [];
    return !remainingIds.includes(currentUser.id);
  };

  /**
   * The banner naming whoever won.
   *
   * Handles rather than real names, and not a cosmetic choice: this screen reads
   * `GET /api/games/{id}`, which sits under a `permitAll` prefix. It used to answer with the
   * Game entity, so every roster member's email, real name, postal address and date of birth
   * came back to anybody who could name a game id — and this banner was printing the real name
   * because it was there. The endpoint sends handles now, which is also what the Game Progress
   * board on the very same screen has always shown.
   */
  const getWinnerDisplay = () => {
    if (!game || !isGameCompleted()) return null;
    const status = game.gameStatus || game.status;

    if (status === 'COMPLETED_WINNER' && game.remainingPlayers?.length === 1) {
      const winner = game.remainingPlayers[0];
      return { type: 'single', name: winner.displayName, isYou: winner.id === currentUser?.id };
    }

    if (status === 'COMPLETED_TIE' && game.remainingPlayers?.length >= 2) {
      const names = game.remainingPlayers.map(p => p.displayName);
      const isYou = game.remainingPlayers.some(p => p.id === currentUser?.id);
      // Everyone who shared it apart from you, worked out here by id.
      //
      // The banner used to do this itself by dropping any name *containing* your first
      // name, which is wrong in both directions and quietly: "Dan" removes "Danielle",
      // "Al" removes "Alice" and "Callum", and two players who happen to share a first
      // name remove each other - leaving the sentence to render as "You shared the
      // victory with !". An id is the thing that actually identifies a player, and the
      // rows carry one.
      //
      // It cannot come back empty: this branch needs two remaining players, a Game's
      // players are a Set so they are distinct, and only one of them can be you. There is
      // deliberately no fallback for a case a valid response cannot produce - the mock
      // backend refuses a body with a duplicated player, which is how that was settled.
      const others = game.remainingPlayers
        .filter(p => p.id !== currentUser?.id)
        .map(p => p.displayName);
      return { type: 'shared', names, others, isYou };
    }

    return null;
  };

  useEffect(() => {
    loadAllData();
    // Reload only when the game changes; loadAllData is recreated every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  // Where the caller wants to land — the league screen sends a live game to the
  // picks tab and a finished one to Game Progress. Only a starting position: the
  // effect below still overrides it once the game itself says otherwise.
  useEffect(() => {
    setActiveTab(initialTab || 'picks');
  }, [gameId, initialTab]);

  useEffect(() => {
    if (game) {
      if (isGameCompleted() || isEliminated()) {
        setActiveTab('progress');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, currentUser]);

  useEffect(() => {
    if (game && allPicks.length > 0) {
      const weekPick = allPicks.find(p => p.gameWeek === game.gameWeek);
      setCurrentPick(weekPick || null);
    } else if (game && allPicks.length === 0) {
      setCurrentPick(null);
    }
  }, [game, allPicks]);

  /**
   * Everything this page shows, in one pass.
   *
   * `silent` skips the full-page spinner. A first visit has nothing to show and should
   * say so; a refresh triggered by something the player just did on this page already
   * has the whole screen rendered, and blanking it out to "Loading game..." for the
   * length of a reload is what made accepting a split prize look like the game had
   * hung. Every panel that calls back into here after an action passes silent.
   */
  const loadAllData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const gameData = await gamesApi.getGame(gameId);
      setGame(gameData);

      await Promise.all([
        loadFixtures(gameData.gameWeek, gameData.tournament?.id),
        loadUsedTeams(),
        loadAllPicks(),
        loadGameweekStatus(),
      ]);
    } catch (err) {
      console.error('Error loading game:', err);
      setError('Failed to load game data');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Panels that act and then refresh: the screen is already up, so never blank it.
  const refreshQuietly = () => loadAllData({ silent: true });

  const loadFixtures = async (gameWeek, tournamentId) => {
    try {
      const fixturesData = await gamesApi.getFixtures(gameId, gameWeek);
      setFixtures(fixturesData);
      loadTeamForm(fixturesData, tournamentId);
    } catch (err) {
      console.error('Error loading fixtures:', err);
    }
  };

  // Bulk-fetch recent form for every team shown this gameweek, so the pick screen
  // can render it without one request per team. Best-effort - a failure here should
  // never block picking, so errors are swallowed after logging.
  const loadTeamForm = async (fixturesData, tournamentId) => {
    try {
      const teamIds = Array.from(new Set(
        fixturesData.flatMap(entry => {
          const fixture = entry.fixture ?? entry;
          return [fixture.homeTeam?.id, fixture.awayTeam?.id].filter(Boolean);
        })
      ));
      if (teamIds.length === 0) return;

      // The tournament's match week, taken off a fixture rather than from game.gameWeek.
      // Those two are not the same number: a game counts its own weeks from whenever it
      // started, so game week 1 can be match week 7. The form cutoff is a real-world
      // instant, so it has to be the real-world week.
      const first = fixturesData[0]?.fixture ?? fixturesData[0];
      const matchWeek = first?.matchWeek;
      if (matchWeek == null) return;

      // Scoped to this game's tournament, and computed as of this week - without either,
      // a side that played elsewhere shows that competition's results, and a closed week
      // re-renders with results that came after it. See teamFormApi.js.
      setTeamForm(await fetchTeamForms(teamIds, tournamentId, matchWeek, 5));
    } catch (err) {
      console.error('Error loading team form:', err);
    }
  };

  const loadUsedTeams = async () => {
    try {
      setUsedTeams(await gamesApi.getUsedTeams(gameId, currentUser.id));
    } catch (err) {
      console.error('Error loading used teams:', err);
    }
  };

  const loadAllPicks = async () => {
    try {
      setAllPicks(await gamesApi.getSelections(gameId, currentUser.id));
    } catch (err) {
      console.error('Error loading picks:', err);
    }
  };

  const loadGameweekStatus = async () => {
    try {
      setGameweekStatus(await gamesApi.getGameweekStatus(gameId, currentUser.id));
    } catch (err) {
      console.error('Error loading gameweek status:', err);
    }
  };

  const areFixturesLocked = () => {
    if (gameweekStatus) return gameweekStatus.locked || !gameweekStatus.canMakePicks;
    return fixtures.length > 0 && fixtures.every(f => f.locked);
  };

  /**
   * Picks are in and the round is being played (or has been).
   *
   * Narrower than areFixturesLocked, which is also true of a week not yet open for picks
   * (UPCOMING) and a voided one. Those still show the pick screen, because its banner is
   * what explains why nothing can be picked; a locked round has nothing left to pick and
   * the screen reorders around the board instead. Read from the server's answer and
   * nothing else, so an admin reopening picks puts the pick screen back.
   */
  const isRoundLocked = () => ['LOCKED', 'CLOSED'].includes(gameweekStatus?.status);

  const getLockedReason = () => {
    if (!gameweekStatus) return 'Fixtures are locked';
    switch (gameweekStatus.status) {
      case 'LOCKED': return 'Picks are locked - awaiting results';
      case 'CLOSED': return 'This gameweek is closed';
      case 'VOIDED': return 'This gameweek has been voided';
      case 'UPCOMING': return 'This gameweek is not yet open for picks';
      default: return gameweekStatus.message || 'Fixtures are locked';
    }
  };

  const isTeamUsed = (teamId) => usedTeams.some(t => t.id === teamId);
  const isManager = () => game?.manager?.id === currentUser.id;

  // Teams used in previous gameweeks only — derived from allPicks, excluding current week.
  // This is authoritative: no ID type-mismatch issues since it comes from the same picks array.
  const previouslyUsedTeams = allPicks
    .filter(p => p.gameWeek !== game?.gameWeek)
    .map(p => p.team ?? { id: p.teamId, teamName: p.teamName, name: p.teamName });

  const handleSelectTeam = (fixtureEntry, team) => {
    if (team === null) {
      setSelectedTeam(null);
      setSelectedFixtureEntry(null);
      return;
    }
    if (isTeamUsed(team.id)) return;
    setSelectedFixtureEntry(fixtureEntry);
    setSelectedTeam(team);
  };

  const handleSubmitPick = async () => {
    if (!selectedTeam || !selectedFixtureEntry || submitting) return;
    setError('');
    setSubmitting(true);
    try {
      await gamesApi.selectTeam(gameId, currentUser.id, {
        teamId: selectedTeam.id,
        gameFixtureEntryId: selectedFixtureEntry.id
      });
      // Quietly: the fixtures stay on screen and the team just confirmed turns green
      // where it was tapped. A full reload blanked the list to a spinner and brought
      // it back, which is the opposite of confirming something.
      await refreshQuietly();
      setSelectedTeam(null);
      setSelectedFixtureEntry(null);
    } catch (err) {
      if (err.name === 'ApiError') {
        setError(err.text || 'Failed to submit pick');
      } else {
        setError('Error submitting pick');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePick = () => {
    setSelectedTeam(currentPick?.team);
    setCurrentPick(null);
  };

  const handleConfirmPick = async () => {
    if (currentPick) {
      if (window.confirm(`Change your pick to ${selectedTeam.name}?`)) await handleSubmitPick();
    } else {
      await handleSubmitPick();
    }
  };


  if (loading) {
    return (
      <div className="gd-page">
        <div className="gd-loading">
          <RefreshCw size={32} className="spin" />
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="gd-page">
        <div className="gd-loading is-error">
          <AlertCircle size={48} />
          <h3>Game Not Found</h3>
        </div>
      </div>
    );
  }

  const eliminated = isEliminated();
  const completed = isGameCompleted();
  const winner = isWinner();
  const winnerInfo = getWinnerDisplay();

  // Only outstanding while a pick can still actually be made.
  const pickNeeded = !eliminated && !completed && !currentPick && !areFixturesLocked();

  // Null for a standalone game, which is the whole distinction now.
  const leagueName = game.miniLeagueTournament?.miniLeague?.name;

  /**
   * Whether anybody could still be seated in this game.
   *
   * Mirrors Game.canManagerAddPlayers on the server, which accepting an invitation runs
   * through and which refuses from IN_PROGRESS on. Getting this wrong is what left the
   * manager being offered "invite people to this game" weeks after the first
   * eliminations - an invitation that could only ever be answered with an error.
   */
  const canTakePlayers = ['OPEN', 'STARTED'].includes(game.gameStatus || game.status);

  /**
   * The tournament's match week for the fixtures on screen.
   *
   * Not game.gameWeek: a game counts its own weeks from whenever it started, so game
   * week 1 can be match week 7. Form is computed as of a real-world instant, so it needs
   * the real-world week - and every fixture in a gameweek comes off the same sheet, so
   * the first one answers for all of them.
   */
  const currentMatchWeek = (fixtures[0]?.fixture ?? fixtures[0])?.matchWeek;

  const roundLocked = isRoundLocked();

  /**
   * The tabs, by key, with what each button says. A missing entry is a tab not offered.
   *
   * "Current Round" rather than "Make Pick": the tab holds the round whether or not a pick
   * is outstanding, and "Make Pick" reads as an instruction you cannot follow once you have
   * picked. The urgency is a badge instead, which disappears when it should. It is withdrawn
   * altogether once the round locks - there is nothing left on it to do.
   *
   * Fixture Planner is offered to everyone, eliminated players included: the spec keeps it as
   * read-only fixture information once you are out, and the server refuses the writes rather
   * than this deciding it.
   */
  const tabs = {
    picks: !eliminated && !completed && !roundLocked && (
      <>
        <Target size={18} /> Current Round
        {pickNeeded && <span className="gd-tab-badge">Pick needed</span>}
      </>
    ),
    // The pick screen's cards kept on screen once picks lock, with the scores as they are
    // entered and who backed each side (backlog item 75). Eliminated players get it too:
    // watching is the one thing left to do, and the picks in it are already public.
    live: roundLocked && !completed && <><Radio size={18} /> Live Round</>,
    planner: <><CalendarClock size={18} /> Fixture Planner</>,
    form: <><Activity size={18} /> Form</>,
    progress: <><Users size={18} /> Game Progress</>,
    'round-stats': areFixturesLocked() && <><BarChart3 size={18} /> Round Stats</>,
    banter: <><MessageSquare size={18} /> Banterboard</>,
  };

  // While picks are open the round is the thing to do, so it leads. Once they lock there is
  // nothing to do but watch, so the order follows that: the talk, then the scores, then who
  // picked what, then the numbers, then looking ahead.
  // The tab on show. A locked round has no pick screen, so asking for it - the default, and
  // what the league screen sends for a live game - lands on the Banterboard instead: once
  // picks are in, the board is where the round is talked about, and the owner wants it to stay
  // the landing page with the Live Round one tap along. Worked out here rather than
  // written back into state, so any other tab somebody chose is left alone and a round an
  // admin reopens shows the pick screen again without anything having to undo a redirect.
  const shownTab = activeTab === 'picks' && roundLocked ? 'banter' : activeTab;

  const tabOrder = roundLocked
    ? ['banter', 'live', 'progress', 'round-stats', 'planner', 'form']
    : ['picks', 'planner', 'form', 'progress', 'round-stats', 'banter'];

  return (
    <div className="gd-page">
      {/* Header */}
      <div className="gd-header">
        <button className="gd-back-btn" onClick={onBack}>
          <ArrowLeft size={18} /> Back
        </button>
        <div className="gd-header-main">
          <h1 className="gd-title">{game.name}</h1>
          <div className="gd-meta">
            <span className="gd-meta-item"><Trophy size={16} color={colors.accentPurple} /> {game.tournamentName || game.tournament?.tournamentName}</span>
            <span className="gd-meta-item"><Calendar size={16} color={colors.accentCyan} /> Week {game.gameWeek}</span>
            <span className="gd-meta-item"><Users size={16} color={colors.successGreen} /> {game.remainingPlayers?.length || 0}/{game.players?.length || 0} remaining</span>
            {/* Which of the two you are in was invisible here, and that is what let
                people believe a game code had put them in a league. */}
            {leagueName && (
              <span className="gd-meta-item"><Shield size={16} color={colors.accentPurple} /> {leagueName}</span>
            )}
            {/* Nothing used to say a game had rolled over: a round where everybody went out
                was followed by one where nobody had, and the reader was left to work out why. */}
            {game.rolloverCount > 0 && (
              <span className="gd-meta-item is-rollover">
                <RotateCcw size={16} /> {rolloverLabel(game)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* The manager's say after a rollover: let people in, take people out. Shows itself only
          while the server says the window is open. */}
      {isManager() && (
        <RolloverPanel game={game} currentUser={currentUser} onChanged={refreshQuietly} />
      )}

      {/* Manager's Gaffer control. Hidden once the game is over - there is nothing
          left to decide - and the panel hides itself when he is out and too late to add. */}
      {isManager() && !completed && (
        <GafferPanel game={game} onChanged={refreshQuietly} />
      )}

      {/* Offered to everyone, worded differently: the manager converts this game,
          anyone else starts a separate league. Hides itself once the game has one. */}
      <CreateLeagueFromGamePanel game={game} isManager={isManager()} onCreated={refreshQuietly} />

      {/* Manager's direct invite, and only while somebody could actually be seated.
          A modal rather than an inline panel because PlayerPicker is class-styled and
          this page is not - Modal is. */}
      {isManager() && canTakePlayers && (
        <div className="gd-invite-row">
          <button
            type="button"
            className="gd-invite-btn"
            onClick={() => setShowInvite(true)}
          >
            Invite people to this game
          </button>
        </div>
      )}

      {/* Under way and closed to arrivals. A league game still has a way in - joining
          the league puts them in the next cycle - so say that instead of nothing. */}
      {isManager() && !canTakePlayers && !completed && leagueName && (
        <p className="gd-invite-closed-note">
          This game is under way, so nobody else can join it. Invite people to{' '}
          {leagueName} from the league page and they will be in the next game.
        </p>
      )}

      {showInvite && (
        <InvitePeopleModal
          scope="GAME"
          scopeId={game.id}
          title={`Invite people to ${game.name}`}
          onClose={() => setShowInvite(false)}
          onInvited={refreshQuietly}
        />
      )}

      {/* Above the tabs, because an outstanding offer decides whether there is any
          point making a pick at all. The panel shows itself only to the last two
          standing, so no condition is needed here beyond the game still being live. */}
      {!completed && (
        <SplitPrizePanel
          game={game}
          currentUser={currentUser}
          pickDeadline={gameweekStatus?.pickDeadline}
          onChanged={refreshQuietly}
        />
      )}

      {/* Winner Banner */}
      {completed && winner && (
        <div className="gd-banner gd-banner-winner">
          <Crown size={64} color="#FFD700" />
          <div>
            <p className="gd-winner-text">
              🏆 {winnerInfo?.type === 'shared' ? 'Congratulations Champions!' : 'Congratulations Champion!'}
            </p>
            <p className="gd-winner-subtext">
              {winnerInfo?.type === 'shared'
                ? `You shared the victory with ${winnerInfo.others.join(' & ')}!`
                : 'You are the Last Man Standing!'}
            </p>
          </div>
        </div>
      )}

      {/* Game Completed Banner - for eliminated players viewing a completed game */}
      {completed && !winner && (
        <div className="gd-banner gd-banner-completed">
          <Award size={48} color="#9C27B0" />
          <div>
            <p className="gd-completed-text">Game Completed</p>
            <p className="gd-completed-sub">
              {winnerInfo?.type === 'shared'
                ? `Winners: ${winnerInfo.names.join(' & ')}`
                : `Winner: ${winnerInfo?.name || 'Unknown'}`}
            </p>
          </div>
        </div>
      )}

      {/* Eliminated Banner - only for active games */}
      {eliminated && !completed && (
        <div className="gd-banner gd-banner-eliminated">
          <Skull size={32} color="#ff1744" />
          <div>
            <p className="gd-eliminated-text">You've Been Eliminated</p>
            <p className="gd-eliminated-subtext">
              Better luck next time! You can still follow the tournament progress below.
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="gd-banner gd-banner-error">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Tab Navigation - which tabs, and in what order, is `tabs` and `tabOrder` above. */}
      <div className="gd-tab-nav">
        {tabOrder.map((key) => tabs[key] && (
          <button
            key={key}
            className={`gd-tab${shownTab === key ? ' active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {tabs[key]}
          </button>
        ))}
      </div>

      {shownTab === 'live' && roundLocked && !completed && (
        <LiveRound gameId={gameId} gameWeek={game.gameWeek} />
      )}

      {shownTab === 'round-stats' && (
        <RoundAnalytics gameId={gameId} gameWeek={game.gameWeek} />
      )}

      {/* Loads and refreshes itself off one planner endpoint. Deliberately not fed this
          page's fixtures or used-teams state: the planner's whole correctness rests on
          availability, the plan and its validity being one join computed together, and
          handing it half of that from a different request is how the two disagree. */}
      {shownTab === 'planner' && (
        <FixturePlanner gameId={gameId} />
      )}

      {/* Asked for by game, which for a league's game resolves to the league's board -
          the same thread the League page shows, not a second one. Only a standalone
          game has a board of its own. The component renders nothing at all if this
          player is not entitled to the board, so there is no empty tab to explain.

          Inside a content-card because the board is drawn for one: its type is the --ink
          set, which is near-black, and this page's shell is a dark purple gradient. The
          same component that reads fine on the League page was dark-on-dark here. The
          card is the surface those colours were chosen against, so the board gets one
          rather than a second palette that would then have to be kept in step. */}
      {shownTab === 'banter' && (
        <div className="gd-board-card content-card">
          <Banterboard
            gameId={gameId}
            tournamentName={game.name}
            currentUserId={currentUser?.id}
            onNavigate={openTabFromPost}
          />
        </div>
      )}

      {/* Form tab — the deep dive. The compact five-game strips stay inline on the
          fixtures, where the pick is actually made; only the full table lives here. */}
      {shownTab === 'form' && (
        <div>
          <AdSlot height={90} onOpen={onOpenSponsor} />
          <TeamFormTable
            tournamentId={game.tournament?.id}
            tournamentName={game.tournament?.tournamentName}
            matchWeek={currentMatchWeek}
          />
          <AdSlot height={250} onOpen={onOpenSponsor} />
        </div>
      )}


      {/* Picks Tab */}
      {shownTab === 'picks' && !eliminated && !completed && !roundLocked && (
        <div>
          {/* Status Banner */}
          {areFixturesLocked() && gameweekStatus?.status && (
            <div className="gd-status-banner" data-status={gameweekStatus.status}>
              <Lock size={20} />
              <span>{getLockedReason()}</span>
            </div>
          )}

          {/* Fixture Selection — owns current pick display, previously used, team select + confirm */}
          {fixtures.length > 0 && (
            <FixtureSelection
              fixtures={fixtures}
              selectedTeam={selectedTeam}
              usedTeams={usedTeams}
              previouslyUsedTeams={previouslyUsedTeams}
              gameWeek={game.gameWeek}
              isLocked={areFixturesLocked()}
              lockedReason={getLockedReason()}
              submitting={submitting}
              onSelectTeam={handleSelectTeam}
              onConfirmPick={handleConfirmPick}
              onChangePick={handleChangePick}
              currentPick={currentPick}
              currentPickTeamId={currentPick ? Number(currentPick.team?.id ?? currentPick.teamId ?? null) : null}
              teamForm={teamForm}
            />
          )}

          {/* No Fixtures Message */}
          {fixtures.length === 0 && (
            <div className="gd-empty">
              <AlertCircle size={48} className="gd-empty-icon" />
              <h3>No Fixtures Available</h3>
              <p>Fixtures for this gameweek haven't been set up yet.</p>
              {/* There was a manager-only "Generate Fixtures" button here, and it could
                  never have worked: it posted to /api/customer/games/{id}/generate-fixtures,
                  which no controller has ever declared, so every click answered 404 and the
                  screen said "Failed to generate fixtures". The contract check in
                  src/__tests__/apiContract.test.js is what found it.

                  Removed rather than pointed somewhere else, because there is nowhere to
                  point it: a game's fixtures are generated when the game is created
                  (GameService.generateFixturesForNewGame) and re-synced by an admin, and
                  there is no customer-facing route that makes more of them. An empty week
                  is a message, not an action a player can take. */}
            </div>
          )}

          {/* Below the fixtures only. Nothing goes above them - an async ad loading in
              above the tap targets is how a rushed pick becomes the wrong pick. */}
          <AdSlot height={250} onOpen={onOpenSponsor} />
        </div>
      )}

      {/* Game Progress.

          Loads and refreshes itself off one progress endpoint, and is deliberately not
          fed this page's game, selections or gameweek state. The board is a history whose
          rows cross-reference each other - what somebody has left is a statement about
          every round above them - and assembling that from four pieces of state that
          arrived at four different moments is how the halves come to disagree. It is also
          the only source that knows which picks may be shown: concealment is the server's
          answer now, not this screen's. */}
      {shownTab === 'progress' && (
        <div>
          <AdSlot height={90} onOpen={onOpenSponsor} />
          <GameProgress gameId={gameId} />
          <AdSlot height={250} onOpen={onOpenSponsor} />
        </div>
      )}
    </div>
  );
}


export default GameDetailsPage;
